import { Inject, Injectable, Injector, OnDestroy } from '@angular/core';
import { Messaging, getToken, isSupported, onMessage } from '@angular/fire/messaging';
import { SwPush } from '@angular/service-worker';
import { RequestService } from '@df/api/request/request.service';
import { LocalStorage } from '@df/browser/storage/decorator/local-storage';
import { SessionStorageService } from '@df/browser/storage/session-storage.service';
import { App } from '@df/core/app';
import { NDfDomGlobals, WINDOW } from '@df/dom/globals';
import { PwaIntegrationService } from '@df/module-pwa/pwa-integration.service';
import { TimeoutService } from '@df/ng/timeout.service';
import { Subscription } from 'rxjs';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { getDocument } from 'ssr-window';
import { PwaNativeBridgeService } from '../pwa-native-bridge.service';
import { NFirebase } from './firebase.interface';
import { FIREBASE_OPTIONS } from './firebase.options';

// PRAWDOPODOBNIE DO WYRZUCENIA
declare module '@df/session/customer/customer.interfaces' {
    namespace Customer {
        interface ICustomerData {
            pwa_tokens?: string[];
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class FirebasePermissionService implements OnDestroy {
    @LocalStorage('firebase-permission-token')
    token: string | null = null;

    token$ = new BehaviorSubject<string | null>(null);

    /** @internal */
    protected messaging?: Messaging;

    customerLoginSubscription?: Subscription;

    /** @internal */
    private messageSubscription?: Subscription;

    /** Observable for foreground messages */
    readonly foregroundMessage$ = new BehaviorSubject<NFirebase.IFCMPayload | null>(null);

    constructor(
        protected app: App,
        protected injector: Injector,
        protected requestService: RequestService,
        protected timeoutService: TimeoutService,
        protected swPush: SwPush,
        private pwaIntegrationService: PwaIntegrationService,
        private sessionStorageService: SessionStorageService,
        private pwaNativeBridgeService: PwaNativeBridgeService,
        @Inject(FIREBASE_OPTIONS) protected options: NFirebase.IOptions,
        @Inject(WINDOW) private window: NDfDomGlobals.Window
    ) {}

    ngOnDestroy(): void {
        this.messageSubscription?.unsubscribe();
        this.customerLoginSubscription?.unsubscribe();
    }

    async init(): Promise<void> {
        if (!(await isSupported()) || !this.pwaIntegrationService.isStandalone) {
            return;
        }
        this.messaging = this.injector.get(Messaging);
        this.setupForegroundMessageListener();
    }

    set remind(remind: boolean) {
        this.sessionStorageService.set('firebase-permission-remind', remind);
        remind && this.customerLoginSubscription?.unsubscribe();
    }
    get remind(): boolean {
        return this.sessionStorageService.get('firebase-permission-remind');
    }

    requestPermission(): void {
        this.getToken();
    }

    protected async getToken() {
        if (!(await isSupported())) {
            this.pwaNativeBridgeService.allowNotification();
        }
        const permission = await Notification.requestPermission();
        switch (permission) {
            case NFirebase.EPermission.Granted:
                try {
                    const serviceWorkerRegistration = await navigator.serviceWorker.getRegistration();
                    if (this.messaging) {
                        this.token = await getToken(this.messaging, {
                            vapidKey: this.options.vapidKey,
                            serviceWorkerRegistration
                        });
                    }

                    this.token$.next(this.token);
                } catch (error) {
                    this.app.logger.error(error);
                }

                if (this.token && this.permissionGranted) {
                    try {
                        await this.sendTokenToServer();

                        // this.modalClose();
                    } catch (error) {
                        this.app.logger.error(error);
                    }
                }

                break;
            default:
                this.token = null;

                break;
        }
    }

    protected sendTokenToServer() {
        return this.requestService.send(undefined, {
            path: NFirebase.EEndpoints.FirebaseSunbscribe,
            data: <NFirebase.IData>{
                userToken: this.token
            }
        });
    }

    get permissionGranted() {
        if ('Notification' in window && Notification.permission === 'granted') {
            return true;
        }

        return false;
    }

    get permissionDenied() {
        if ('Notification' in window && Notification.permission === 'denied') {
            return true;
        }

        return false;
    }

    protected permissionStatus(status: NotificationPermission = NFirebase.EPermission.Default): boolean {
        if ('Notification' in window && Notification.permission === status) {
            return true;
        }

        return false;
    }

    reload(): void {
        const document = getDocument();

        document.location.reload();
    }

    /**
     * Sets up foreground message listener for zoneless Angular compatibility
     * @private
     */
    private setupForegroundMessageListener(): void {
        if (!this.messaging) {
            return;
        }

        // Set up foreground message listener
        // In zoneless Angular, BehaviorSubject automatically triggers change detection
        const unsubscribe = onMessage(this.messaging, (payload: NFirebase.IFCMPayload) => {
            console.warn('[Firebase] Foreground message received:', payload);

            // Simply update the BehaviorSubject - zoneless Angular will handle change detection
            this.foregroundMessage$.next(payload);
            this.handleForegroundMessage(payload);
        });

        // Store the unsubscribe function for cleanup
        this.messageSubscription = new Subscription(() => unsubscribe());
    }

    /**
     * Handles foreground messages
     * @private
     */
    private handleForegroundMessage(payload: NFirebase.IFCMPayload): void {
        // Handle the foreground message here
    }
}
